'use client';

import { useIntl } from 'react-intl';
import { Divider } from '@packages/shared/src/ui/Divider/Divider';
import { usePriceFormater } from '@packages/shared/src/hooks/usePriceFormater/usePriceFormater';
import { Content } from '../Content/Content';

export type SideBySideWithIconBadgeProps = {
  /**
   * the single installment value from the calculation result as float (e.g. 13.37)
   */
  singleInstallment: number;
  /**
   * the currency string from the calculation result
   */
  currency: string;
  /**
   * the number of installments from the calculation result
   */
  numberOfInstallments: number;
  /**
   * open dialog handler
   */
  openInstallmentCalulatorHandler: () => void;
};

/**
 * Promotion component side by side with icon badge
 *
 * component to promote the installment calculator in a side by side layout version with an icon badge
 *
 * */
export const SideBySideWithIconBadge = ({
  singleInstallment,
  currency,
  numberOfInstallments,
  openInstallmentCalulatorHandler,
}: SideBySideWithIconBadgeProps) => {
  const { formatMessage } = useIntl();
  const formatPrice = usePriceFormater('INSTALLMENTCALCULATOR');

  return (
    <div className="relative mt-[0.938rem]">
      <div className="border-color-grey bg-color-background-base relative block rounded border">
        <div className="absolute top-[-0.938rem] left-1/2 block -translate-x-1/2">
          {/* a white line to have space between cirle and border */}
          <div className="bg-color-background-base absolute top-[0.938rem] left-1/2 block h-1 w-[3.75rem] -translate-x-1/2 -translate-y-0.5" />
          <div className="bg-color-primary relative flex size-[2.875rem] items-center justify-center rounded-full">
            {/* translate-x is an optical correction to fit the icon into the circle */}
            <div className="icon-emp-installmentplan text-color-text-light size-4 translate-x-[0.1rem]" />
          </div>
        </div>
        <div className="flex flex-col gap-1 py-1">
          <div className="px2 flex flex-row flex-nowrap gap-5">
            <Content
              smallText={formatMessage({
                id: 'ic.promotion.sideBySideWithIconBadge.from',
                defaultMessage: 'oder ab',
              })}
              bigText={formatPrice(singleInstallment, currency)}
            />
            <Content
              smallText={formatMessage({
                id: 'ic.promotion.sideBySideWithIconBadge.installments.prefix',
                defaultMessage: 'in bequemen',
              })}
              bigText={formatMessage(
                {
                  id: 'ic.promotion.sideBySideWithIconBadge.installments',
                  defaultMessage: '{installments} Raten',
                },
                {
                  installments: numberOfInstallments,
                },
              )}
            />
          </div>
          <Divider />
          <button
            type="button"
            className="text-size-body-sm block cursor-pointer text-center"
            onClick={() => openInstallmentCalulatorHandler()}
          >
            {formatMessage({
              id: 'ic.promotion.sideBySideWithIconBadge.cta',
              defaultMessage: 'Wunschrate berechnen',
            })}
            {/* this extends the button click area over the whole component */}
            <span className="absolute inset-0" />
          </button>
        </div>
      </div>
    </div>
  );
};
